import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchActiveRentals,
  fetchRentalTenants,
  createRentalTenant,
  updateRentalTenant,
  deleteRentalTenant
} from '../../../Redux/Actions/actions';
import { 
  FaFileContract,
  FaMapMarkerAlt,
  FaUsers,
  FaTimes,
  FaArrowLeft,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave
} from 'react-icons/fa';
import styles from './TenantManagement.module.css';

const defaultForm = {
  fullName: '',
  dni: '',
  cuit: '',
  email: '',
  phone: '',
  tenantType: 'main',
  occupation: '',
  employer: '',
  monthlyIncome: '',
  documentsText: '',
  guarantorFullName: '',
  guarantorDni: '',
  guarantorRelationship: '',
  guarantorPhone: ''
};

const tenantTypeLabels = {
  main: 'Inquilino principal',
  'co-tenant': 'Co-inquilino',
  guarantor: 'Garante'
};

const TenantManagement = ({ onClose }) => {
  const dispatch = useDispatch();
  
  const {
    activeRentals = [],
    rentalTenants = [],
    rentalsLoading: loading = false
  } = useSelector(state => state || {});

  const [selectedContract, setSelectedContract] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState(defaultForm);
  const [editingTenant, setEditingTenant] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(fetchActiveRentals());
  }, [dispatch]);

  useEffect(() => {
    if (selectedContract) {
      console.log('Cargando inquilinos para rentalId:', selectedContract.id);
      dispatch(fetchRentalTenants(selectedContract.id));
    }
  }, [dispatch, selectedContract]);

  const handleSelectContract = (contract) => {
    // El ID del contrato es el UUID en contract.id, NO propertyId
    console.log('Contrato seleccionado - Estructura:', contract);
    console.log('Usando ID para peticiones:', contract.id);
    setSelectedContract(contract);
    setFormData(defaultForm);
    setEditingTenant(null);
  };

  const handleBackToContracts = () => {
    setSelectedContract(null);
    setFormData(defaultForm);
    setEditingTenant(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData(defaultForm);
    setEditingTenant(null);
  };

  const buildPayload = () => {
    const documents = formData.documentsText
      ? formData.documentsText.split(',').map(d => d.trim()).filter(Boolean)
      : [];


    const guarantorData = formData.guarantorFullName
      ? {
          fullName: formData.guarantorFullName,
          dni: formData.guarantorDni,
          relationship: formData.guarantorRelationship,
          phone: formData.guarantorPhone
        }
      : undefined;

    return {
      fullName: formData.fullName,
      dni: formData.dni,
      cuit: formData.cuit,
      email: formData.email,
      phone: formData.phone,
      tenantType: formData.tenantType,
      occupation: formData.occupation,
      employer: formData.employer,
      monthlyIncome: formData.monthlyIncome ? Number(formData.monthlyIncome) : undefined,
      documents,
      guarantorData
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.dni) {
      alert('Nombre completo y DNI son obligatorios');
      return;
    }

    setIsSubmitting(true);
    try {
      const rentalId = selectedContract.id;
      console.log('Creando inquilino para rentalId:', rentalId);
      const payload = buildPayload();
      if (editingTenant) {
        await dispatch(updateRentalTenant(editingTenant.id, payload));
      } else {
        await dispatch(createRentalTenant(rentalId, payload));
      }
      resetForm();
      dispatch(fetchRentalTenants(rentalId));
    } catch (error) {
      console.error('Error:', error);
      alert('Error al guardar inquilino');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setFormData({
      fullName: tenant.fullName || '',
      dni: tenant.dni || '',
      cuit: tenant.cuit || '',
      email: tenant.email || '',
      phone: tenant.phone || '',
      tenantType: tenant.tenantType || 'main',
      occupation: tenant.occupation || '',
      employer: tenant.employer || '',
      monthlyIncome: tenant.monthlyIncome || '',
      documentsText: Array.isArray(tenant.documents) ? tenant.documents.join(', ') : '',
      guarantorFullName: tenant.guarantorData?.fullName || '',
      guarantorDni: tenant.guarantorData?.dni || '',
      guarantorRelationship: tenant.guarantorData?.relationship || '',
      guarantorPhone: tenant.guarantorData?.phone || ''
    });
  };

  const handleDelete = async (tenant) => {
    if (!window.confirm(`¿Eliminar a ${tenant.fullName}?`)) return;
    try {
      const rentalId = selectedContract.id;
      console.log('Eliminando inquilino del rentalId:', rentalId);
      await dispatch(deleteRentalTenant(tenant.id));
      dispatch(fetchRentalTenants(rentalId));
    } catch (error) {
      console.error('Error:', error);
      alert('Error al eliminar inquilino');
    }
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getFilteredContracts = () => {
    if (!searchTerm) return activeRentals;
    const term = searchTerm.toLowerCase();
    return activeRentals.filter(contract => 
      contract.street?.toLowerCase().includes(term) ||
      contract.locality?.toLowerCase().includes(term) ||
      contract.id?.toLowerCase().includes(term)
    );
  };

  const filteredContracts = getFilteredContracts();

  // Vista de selección de contratos
  if (!selectedContract) {
    if (loading && activeRentals.length === 0) {
      return (
        <div className={styles.container}>
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
            <p>Cargando contratos...</p>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <FaFileContract className={styles.titleIcon} />
            <h2 className={styles.title}>Seleccionar Contrato</h2>
            <p className={styles.subtitle}>Los inquilinos se gestionan por contrato de alquiler</p>
          </div>
          {onClose && (
            <button onClick={onClose} className={styles.closeBtn}>
              <FaTimes />
            </button>
          )}
        </div>

        <div className={styles.searchSection}>
          <div className={styles.searchBox}>
            <input
              type="text"
              placeholder="Buscar por dirección o ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>

        {filteredContracts.length === 0 ? (
          <div className={styles.emptyState}>
            <FaFileContract className={styles.emptyIcon} />
            <h3>No hay contratos activos</h3>
            <p>No se encontraron contratos para gestionar inquilinos</p>
          </div>
        ) : (
          <div className={styles.contractsList}>
            {filteredContracts.map((contract) => (
              <div key={contract.id} className={styles.contractCard}>
                <div className={styles.contractHeader}>
                  <FaMapMarkerAlt className={styles.contractIcon} />
                  <div className={styles.contractInfo}>
                    <h3 className={styles.contractAddress}>
                      {contract.street} {contract.number}, {contract.locality}
                    </h3>
                    <p className={styles.contractId}>ID: {contract.id}</p>
                  </div>
                </div>

                <div className={styles.contractDetails}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Valor mensual:</span>
                    <span className={styles.detailValue}>{formatCurrency(contract.currentRentValue || contract.rentValue)}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Estado:</span>
                    <span className={`${styles.badge} ${styles.active}`}>{contract.contractStatus || 'Activo'}</span>
                  </div>
                </div>

                <div className={styles.contractActions}>
                  <button
                    onClick={() => handleSelectContract(contract)}
                    className={styles.primaryBtn}
                  >
                    <FaUsers /> Gestionar Inquilinos
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Vista de gestión de inquilinos del contrato seleccionado
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <button onClick={handleBackToContracts} className={styles.backBtn}>
            <FaArrowLeft />
          </button>
          <div>
            <h2 className={styles.title}>Inquilinos del Contrato</h2>
            <p className={styles.subtitle}>
              {selectedContract.street} {selectedContract.number}, {selectedContract.locality}
            </p>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className={styles.closeBtn}>
            <FaTimes />
          </button>
        )}
      </div>

      <div className={styles.contentGrid}>
        {/* Formulario */}
        <div className={styles.formCard}>
          <h3 className={styles.formTitle}>
            {editingTenant ? 'Editar Inquilino' : 'Nuevo Inquilino'}
          </h3>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Nombre Completo *</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label>DNI *</label>
                <input
                  type="text"
                  name="dni"
                  value={formData.dni}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>CUIT</label>
                <input
                  type="text"
                  name="cuit"
                  value={formData.cuit}
                  onChange={handleInputChange}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Tipo de Inquilino</label>
                <select
                  name="tenantType"
                  value={formData.tenantType}
                  onChange={handleInputChange}
                >
                  <option value="main">Inquilino principal</option>
                  <option value="co-tenant">Co-inquilino</option>
                  <option value="guarantor">Garante</option>
                </select>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Teléfono</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Ocupación</label>
                <input
                  type="text"
                  name="occupation"
                  value={formData.occupation}
                  onChange={handleInputChange}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Empleador</label>
                <input
                  type="text"
                  name="employer"
                  value={formData.employer}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Ingreso Mensual</label>
                <input
                  type="number"
                  name="monthlyIncome"
                  value={formData.monthlyIncome}
                  onChange={handleInputChange}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Documentos (URLs separadas por coma)</label>
                <input
                  type="text"
                  name="documentsText"
                  value={formData.documentsText}
                  onChange={handleInputChange}
                  placeholder="url1.jpg, url2.jpg"
                />
              </div>
            </div>

            <div className={styles.formSection}>
              <h4>Datos del Garante (opcional)</h4>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Nombre Completo</label>
                  <input
                    type="text"
                    name="guarantorFullName"
                    value={formData.guarantorFullName}
                    onChange={handleInputChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>DNI</label>
                  <input
                    type="text"
                    name="guarantorDni"
                    value={formData.guarantorDni}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Relación</label>
                  <input
                    type="text"
                    name="guarantorRelationship"
                    value={formData.guarantorRelationship}
                    onChange={handleInputChange}
                    placeholder="ej: Padre, Madre, Hermano"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Teléfono</label>
                  <input
                    type="text"
                    name="guarantorPhone"
                    value={formData.guarantorPhone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            <div className={styles.formActions}>
              {editingTenant && (
                <button type="button" onClick={resetForm} className={styles.btnSecondary}>
                  Cancelar
                </button>
              )}
              <button type="submit" disabled={isSubmitting} className={styles.btnPrimary}>
                <FaSave /> {isSubmitting ? 'Guardando...' : (editingTenant ? 'Actualizar' : 'Agregar')}
              </button>
            </div>
          </form>
        </div>

        {/* Lista de inquilinos */}
        <div className={styles.tenantsListCard}>
          <h3 className={styles.listTitle}>
            <FaUsers /> Inquilinos ({rentalTenants.length})
          </h3>
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner}></div>
              <p>Cargando inquilinos...</p>
            </div>
          ) : rentalTenants.length === 0 ? (
            <div className={styles.emptyState}>
              <FaUsers className={styles.emptyIcon} />
              <p>No hay inquilinos registrados</p>
            </div>
          ) : (
            <div className={styles.tenantsList}>
              {rentalTenants.map((tenant) => (
                <div key={tenant.id} className={styles.tenantItem}>
                  <div className={styles.tenantInfo}>
                    <h4>{tenant.fullName}</h4>
                    <span className={`${styles.badge} ${styles[tenant.tenantType]}`}>
                      {tenantTypeLabels[tenant.tenantType]}
                    </span>
                    <p className={styles.tenantDetails}>
                      DNI: {tenant.dni} {tenant.email && `• ${tenant.email}`}
                    </p>
                  </div>
                  <div className={styles.tenantActions}>
                    <button onClick={() => handleEdit(tenant)} className={styles.btnIcon}>
                      <FaEdit />
                    </button>
                    <button onClick={() => handleDelete(tenant)} className={styles.btnIconDanger}>
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantManagement;
